import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import EventImagePicker from "../CreateEvent/EventImagePicker/EventImagePicker";
import { LEGACY_IMAGE_OPTIONS } from "../CreateEvent/legacyImageOptions";
import { searchLocations } from "../../../api/eventLocations";
import "./EditEventsTab.css";

// GraphQL — defined inline so this component owns its data shape contract.
const EDITABLE_EVENTS = gql`
  query EditableSocialEvents(
    $sinceDate: String
    $beforeDate: String
    $limit: Int
  ) {
    editableSocialEvents(
      sinceDate: $sinceDate
      beforeDate: $beforeDate
      limit: $limit
    ) {
      id
      title
      date
      time
      description
      generalpublic
      location
      privateLocation
      imgKey
      length
      groupmeImg
      status
      password
    }
  }
`;

const UPDATE_SOCIAL_EVENT = gql`
  mutation UpdateSocialEvent(
    $socialEventId: ID!
    $input: SocialEventUpdateInput!
  ) {
    updateSocialEvent(socialEventId: $socialEventId, input: $input) {
      id
      title
      date
      time
      description
      generalpublic
      location
      privateLocation
      imgKey
      length
      groupmeImg
      status
      password
    }
  }
`;

const DELETE_SOCIAL_EVENT = gql`
  mutation DeleteSocialEvent($socialEventId: ID!) {
    deleteSocialEvent(socialEventId: $socialEventId)
  }
`;

// ---- image helpers (mirror PhotoManagerTab) ----
// Build a small-thumbnail URL by appending `_thumb.jpg`. Backend backfill
// writes a 320px max-dimension JPEG sibling for every photo. Some legacy
// `imgKey` values lack the `main/` prefix — use them as-is.
const S3_BASE = "https://s3.amazonaws.com/social-images-public/";
const buildOriginalImgUrl = (imgKey) => (imgKey ? `${S3_BASE}${imgKey}` : null);
// The thumbnail backfill only writes _thumb.jpg siblings for files under
// main/<activity>/<file>. Legacy paths like `main/121.jpg`, `121.jpg`, or
// anything without an activity folder don't have thumbnails — return null so
// callers render the original directly, avoiding spurious 404s in DevTools.
const HAS_ACTIVITY_PREFIX_RE = /^main\/[^/]+\/[^/]+/;
const buildThumbImgUrl = (imgKey) => {
  if (!imgKey) return null;
  if (!HAS_ACTIVITY_PREFIX_RE.test(imgKey)) return null;
  return `${S3_BASE}${imgKey}_thumb.jpg`;
};
// onError handler that swaps to the original URL exactly once. The
// `data-fallback-tried` flag prevents an infinite loop if the original is
// also broken (browser retries onError on each failed src change).
const handleImgErrorFallback = (e, originalSrc) => {
  if (!e || !e.target || !originalSrc) return;
  if (e.target.dataset.fallbackTried === "1") return;
  e.target.dataset.fallbackTried = "1";
  e.target.src = originalSrc;
};

// Pull the activity slug (e.g. "volleyball") out of a socialEventId of the
// form "YYYY-MM-DD-<activity-slug>" (with possible additional suffixes).
// Used as a fallback caption when an event has no imgKey.
const activityFromSocialEventId = (id) => {
  if (!id || typeof id !== "string") return "";
  const m = id.match(/^\d{4}-\d{2}-\d{2}-([^-]+)/);
  return m ? m[1] : "";
};

// ---- date helpers ----
// Backend defaults sinceDate to (today − 2 calendar days) in UTC. We mirror
// that on the client by computing UTC YYYY-MM-DD so the URL/state matches what
// the server would have chosen if we sent nothing. Using UTC also avoids
// off-by-one issues for users west of UTC at late hours.
const todayUtcYmd = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const ymdMinusDays = (ymd, days) => {
  // Parse YYYY-MM-DD as a UTC midnight, subtract days, format back as UTC YYYY-MM-DD.
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const ms = Date.UTC(y, m - 1, d) - days * 24 * 60 * 60 * 1000;
  const dt = new Date(ms);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// Convert a stored event date (could be "Month DD, YYYY" or YYYY-MM-DD or ISO)
// into "YYYY-MM-DD" for the date input. Best-effort: returns "" on failure.
const toDateInputValue = (raw) => {
  if (!raw) return "";
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Convert a stored event time (e.g. "6:00 PM" or "18:00") to "HH:MM" for the
// time input. Returns "" on failure.
const toTimeInputValue = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  // Already HH:MM or HH:MM:SS
  const m24 = s.match(/^(\d{1,2}):(\d{2})/);
  if (m24 && !/AM|PM/i.test(s)) {
    const h = String(parseInt(m24[1], 10)).padStart(2, "0");
    return `${h}:${m24[2]}`;
  }
  // 12-hour with AM/PM
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10) % 12;
    if (/PM/i.test(m12[3])) h += 12;
    return `${String(h).padStart(2, "0")}:${m12[2]}`;
  }
  return "";
};

// Build a stable "edit draft" from a server event row.
const draftFromEvent = (ev) => ({
  title: ev.title || "",
  date: toDateInputValue(ev.date),
  time: toTimeInputValue(ev.time),
  description: ev.description || "",
  generalpublic: !!ev.generalpublic,
  location: ev.location || "",
  privateLocation: ev.privateLocation || "",
  imgKey: ev.imgKey || "",
  length: ev.length != null ? String(ev.length) : "",
  groupmeImg: ev.groupmeImg || "",
  status: ev.status || "",
  password: ev.password != null ? String(ev.password) : "",
});

// Build the same shape from the original row so we can diff before sending —
// only changed fields are sent to the mutation.
const originalShapeFromEvent = (ev) => ({
  title: ev.title || "",
  date: toDateInputValue(ev.date),
  time: toTimeInputValue(ev.time),
  description: ev.description || "",
  generalpublic: !!ev.generalpublic,
  location: ev.location || "",
  privateLocation: ev.privateLocation || "",
  imgKey: ev.imgKey || "",
  length: ev.length != null ? String(ev.length) : "",
  groupmeImg: ev.groupmeImg || "",
  status: ev.status || "",
  password: ev.password != null ? String(ev.password) : "",
});

// --- Location autocomplete subcomponent ---
// Owns its own debounce + dropdown state so the parent's edit form stays
// declarative. Calls onChange(value) any time the admin types or picks; the
// value sent is the freeform "Title, Address" string the rest of the app
// expects (mirrors CreateEvent.js's hardcoded list shape).
const LocationAutocomplete = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Sync external value changes (e.g. when starting edit on a different event).
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Debounced fetch when the box is focused/open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const q = (query || "").trim();
    const handle = setTimeout(() => {
      searchLocations(q)
        .then((resp) => {
          if (cancelled) return;
          setResults(Array.isArray(resp && resp.results) ? resp.results : []);
        })
        .catch((err) => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn("Location search failed:", err);
          setResults([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handlePick = (r) => {
    const composed = `${r.title || ""}${r.address ? ", " + r.address : ""}`;
    setQuery(composed);
    onChange(composed);
    setOpen(false);
  };

  const sortedResults = useMemo(
    () =>
      [...(results || [])].sort(
        (a, b) => (b?.favorite ? 1 : 0) - (a?.favorite ? 1 : 0)
      ),
    [results]
  );

  return (
    <div className="edit-events-tab__autocomplete" ref={containerRef}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type to search locations…"
      />
      {open && sortedResults.length > 0 && (
        <ul className="edit-events-tab__autocomplete-list">
          {sortedResults.map((r, idx) => (
            <li key={`${r.title}-${idx}`} onClick={() => handlePick(r)}>
              {r.favorite ? (
                <span
                  className="edit-events-tab__star"
                  aria-label="Favorite"
                  title="Favorite"
                >
                  ★
                </span>
              ) : null}
              {r.title}
              {r.address ? (
                <span style={{ color: "#6b7280", marginLeft: 4 }}>
                  — {r.address}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {open && sortedResults.length === 0 && (query || "").trim().length > 0 && (
        <ul className="edit-events-tab__autocomplete-list">
          <li className="edit-events-tab__autocomplete-empty">
            No matches — keep typing to use as freeform value
          </li>
        </ul>
      )}
    </div>
  );
};

const EditEventsTab = () => {
  const [sinceDate, setSinceDate] = useState(() =>
    ymdMinusDays(todayUtcYmd(), 2)
  );

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [toast, setToast] = useState(null);

  // View-mode toggle: 'grid' is the default per user request; 'list' is the
  // alternative table view. In grid mode, clicking a card opens an edit modal
  // (same `editingId`/`editDraft` state — no duplication).
  const [viewMode, setViewMode] = useState("grid");

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const { data, loading, error, refetch } = useQuery(EDITABLE_EVENTS, {
    variables: { sinceDate, limit: 200 },
    fetchPolicy: "cache-and-network",
  });

  const [updateSocialEvent] = useMutation(UPDATE_SOCIAL_EVENT);
  const [deleteSocialEvent] = useMutation(DELETE_SOCIAL_EVENT);

  // Typed-confirmation modal state for destructive deletes.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // Escape closes the grid edit modal — but only when the delete-confirm
  // modal isn't stacked on top. The delete confirm has its own dismiss flow
  // (typed "delete" + Cancel button) and we don't want Escape to nuke both
  // modals at once when the user just wanted to dismiss the confirm. Also
  // blocks Escape during a save in flight — bailing mid-PATCH would be
  // jarring and could leave the form in a confusing state.
  useEffect(() => {
    if (viewMode !== "grid" || !editingId) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (savingId) return; // mid-save, ignore
      if (confirmingDeleteId) return; // delete confirm owns Escape while open
      setEditingId(null);
      setEditDraft(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [viewMode, editingId, savingId, confirmingDeleteId]);

  const requestDelete = (ev) => {
    setConfirmingDeleteId(ev.id);
    setConfirmText("");
  };

  const cancelDelete = () => {
    setConfirmingDeleteId(null);
    setConfirmText("");
  };

  const performDelete = async (ev) => {
    const id = ev.id;
    setDeletingId(id);
    try {
      await deleteSocialEvent({ variables: { socialEventId: id } });
      // Refetch the list so the deleted row disappears.
      await refetch();
      showToast("success", `Deleted "${ev.title || id}"`);
      cancelDelete();
    } catch (err) {
      const msg =
        (err && err.graphQLErrors && err.graphQLErrors[0] && err.graphQLErrors[0].message) ||
        (err && err.message) ||
        "Delete failed";
      showToast("error", msg);
    } finally {
      setDeletingId(null);
    }
  };

  // The query is the source of truth for the list. Local state only tracks
  // edit-in-progress; once the mutation returns, Apollo's normalized cache
  // updates the row in `data` and the table re-renders.
  const events = useMemo(() => {
    const list = (data && data.editableSocialEvents) || [];
    // Server promises desc by socialEventId, but defensively re-sort.
    return [...list].sort((a, b) =>
      String(b.id || "").localeCompare(
        String(a.id || "")
      )
    );
  }, [data]);

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setEditDraft(draftFromEvent(ev));
    setRowError((prev) => {
      const next = { ...prev };
      delete next[ev.id];
      return next;
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const updateDraftField = (field, value) => {
    setEditDraft((d) => ({ ...d, [field]: value }));
  };

  const saveEdit = async (ev) => {
    if (!editDraft) return;
    const id = ev.id;
    const original = originalShapeFromEvent(ev);

    // Diff: only send fields that actually changed.
    const input = {};
    Object.keys(editDraft).forEach((key) => {
      const a = editDraft[key];
      const b = original[key];
      // Normalize booleans + strings for comparison.
      if (typeof a === "boolean" || typeof b === "boolean") {
        if (!!a !== !!b) input[key] = !!a;
      } else if ((a || "") !== (b || "")) {
        input[key] = a;
      }
    });

    if (Object.keys(input).length === 0) {
      cancelEdit();
      showToast("info", "No changes to save.");
      return;
    }

    setSavingId(id);
    try {
      const resp = await updateSocialEvent({
        variables: { socialEventId: id, input },
        // Apollo merges the returned SocialEvent (matched by id) back into the
        // EDITABLE_EVENTS result automatically thanks to normalized caching.
      });
      if (resp && resp.data && resp.data.updateSocialEvent) {
        showToast("success", "Event updated.");
        cancelEdit();
      } else {
        throw new Error("Mutation returned no data");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("updateSocialEvent failed:", err);
      const msg = (err && err.message) || "Update failed.";
      setRowError((prev) => ({ ...prev, [id]: msg }));
      showToast("error", msg);
    } finally {
      setSavingId(null);
    }
  };

  const loadOlder = () => {
    // Strategy B from the spec: just widen the window. Apollo refetches with
    // the new variables and returns everything in the wider range, so the
    // already-loaded items stay visible.
    setSinceDate((prev) => ymdMinusDays(prev, 7));
  };

  // Shared edit-form renderer used by BOTH the inline-row (table view) and
  // the modal (grid view). Same fields, same handlers, same state — just two
  // different containers. Caller is responsible for the surrounding chrome
  // (the wrapping <div className="edit-events-tab__edit-card"> for inline,
  // or the modal body wrapper for grid).
  const renderEditForm = (ev) => {
    const id = ev.id;
    const isSaving = savingId === id;
    const errMsg = rowError[id];
    if (!editDraft) return null;
    return (
      <>
        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>Title</label>
          <input
            type="text"
            value={editDraft.title}
            disabled={isSaving}
            onChange={(e) => updateDraftField("title", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field">
          <label>Date</label>
          <input
            type="date"
            value={editDraft.date}
            disabled={isSaving}
            onChange={(e) => updateDraftField("date", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field">
          <label>Time</label>
          <input
            type="time"
            value={editDraft.time}
            disabled={isSaving}
            onChange={(e) => updateDraftField("time", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>Description</label>
          <textarea
            value={editDraft.description}
            disabled={isSaving}
            onChange={(e) => updateDraftField("description", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full edit-events-tab__checkbox-row">
          <input
            id={`gp-${id}`}
            type="checkbox"
            checked={editDraft.generalpublic}
            disabled={isSaving}
            onChange={(e) =>
              updateDraftField("generalpublic", e.target.checked)
            }
          />
          <label htmlFor={`gp-${id}`}>
            General Public (visible to everyone)
          </label>
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>Location</label>
          <LocationAutocomplete
            value={editDraft.location}
            disabled={isSaving}
            onChange={(v) => updateDraftField("location", v)}
          />
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>Private Location</label>
          <input
            type="text"
            value={editDraft.privateLocation}
            disabled={isSaving}
            onChange={(e) =>
              updateDraftField("privateLocation", e.target.value)
            }
          />
        </div>

        <div className="edit-events-tab__field">
          <label>Length (hours)</label>
          <input
            type="text"
            value={editDraft.length}
            disabled={isSaving}
            onChange={(e) => updateDraftField("length", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field">
          <label>Host ID Number (admin only)</label>
          <input
            type="text"
            value={editDraft.password}
            disabled={isSaving}
            onChange={(e) => updateDraftField("password", e.target.value)}
          />
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>Event Image</label>
          <EventImagePicker
            value={editDraft.imgKey}
            onChange={(v) => updateDraftField("imgKey", v)}
            legacyOptions={LEGACY_IMAGE_OPTIONS}
          />
        </div>

        <div className="edit-events-tab__field edit-events-tab__field--full">
          <label>GroupMe Image URL</label>
          <input
            type="text"
            value={editDraft.groupmeImg}
            disabled={isSaving}
            onChange={(e) => updateDraftField("groupmeImg", e.target.value)}
            placeholder="https://i.groupme.com/..."
          />
        </div>

        <div className="edit-events-tab__field">
          <label>Status</label>
          <input
            type="text"
            value={editDraft.status}
            disabled={isSaving}
            onChange={(e) => updateDraftField("status", e.target.value)}
            placeholder="ok"
            list="edit-events-status-options"
          />
          <datalist id="edit-events-status-options">
            <option value="pending" />
            <option value="ok" />
          </datalist>
          <small style={{ color: "#6b7280", fontSize: 12 }}>
            Common values: <strong>pending</strong> (awaiting host
            confirmation) and <strong>ok</strong> (confirmed).
          </small>
        </div>

        {errMsg && (
          <div className="edit-events-tab__inline-error edit-events-tab__field--full">
            {errMsg}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="edit-events-tab">
      <div className="edit-events-tab__header">
        <h2>Edit Events</h2>
        <div className="edit-events-tab__filter">
          <label htmlFor="edit-events-since">Since:</label>
          <input
            id="edit-events-since"
            type="date"
            value={sinceDate}
            onChange={(e) => setSinceDate(e.target.value)}
          />
          <span className="edit-events-tab__count">
            ({events.length} event{events.length === 1 ? "" : "s"})
          </span>
          <div
            className="edit-events-tab__viewtoggle"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={`edit-events-tab__viewtoggle-btn${
                viewMode === "list"
                  ? " edit-events-tab__viewtoggle-btn--active"
                  : ""
              }`}
              onClick={() => setViewMode("list")}
              aria-pressed={viewMode === "list"}
            >
              List
            </button>
            <button
              type="button"
              className={`edit-events-tab__viewtoggle-btn${
                viewMode === "grid"
                  ? " edit-events-tab__viewtoggle-btn--active"
                  : ""
              }`}
              onClick={() => setViewMode("grid")}
              aria-pressed={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
          <button
            type="button"
            className="edit-events-tab__btn"
            onClick={() => refetch()}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`edit-events-tab__status edit-events-tab__status--${toast.kind === "info" ? "info" : toast.kind}`}
        >
          {toast.message}
        </div>
      )}

      {loading && events.length === 0 && (
        <div className="edit-events-tab__status edit-events-tab__status--info">
          Loading events…
        </div>
      )}

      {error && (
        <div className="edit-events-tab__status edit-events-tab__status--error">
          Could not load events: {error.message || "unknown error"}
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="edit-events-tab__status edit-events-tab__status--info">
          No editable events in this window.
        </div>
      )}

      {events.length > 0 && viewMode === "list" && (
        <div className="edit-events-tab__table-wrap">
          <table className="edit-events-tab__table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Title</th>
                <th>Location</th>
                <th>ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => {
                const id = ev.id;
                const isEditing = editingId === id;
                const isSaving = savingId === id;
                return (
                  <React.Fragment key={id}>
                    <tr
                      className={`edit-events-tab__row${
                        isEditing ? " edit-events-tab__row--editing" : ""
                      }`}
                    >
                      <td className="edit-events-tab__date-col">
                        {ev.date || "—"}
                        <br />
                        <span style={{ color: "#6b7280" }}>
                          {ev.time || ""}
                        </span>
                      </td>
                      <td className="edit-events-tab__title-col">
                        {ev.title || "(untitled)"}
                      </td>
                      <td className="edit-events-tab__location-col" title={ev.location}>
                        {ev.location || "—"}
                      </td>
                      <td className="edit-events-tab__id-col">{id}</td>
                      <td>
                        {isEditing ? (
                          <button
                            type="button"
                            className="edit-events-tab__btn"
                            onClick={cancelEdit}
                            disabled={isSaving}
                          >
                            Close
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="edit-events-tab__btn"
                              onClick={() => startEdit(ev)}
                            >
                              Edit
                            </button>{" "}
                            <button
                              type="button"
                              className="edit-events-tab__btn edit-events-tab__btn--danger"
                              onClick={() => requestDelete(ev)}
                              disabled={deletingId === id}
                            >
                              {deletingId === id ? "Deleting…" : "Delete"}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isEditing && editDraft && (
                      <tr className="edit-events-tab__row--editing">
                        <td colSpan={5} style={{ padding: 0 }}>
                          <div className="edit-events-tab__edit-card">
                            {renderEditForm(ev)}
                            <div className="edit-events-tab__edit-actions">
                              <button
                                type="button"
                                className="edit-events-tab__btn"
                                onClick={cancelEdit}
                                disabled={isSaving}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="edit-events-tab__btn edit-events-tab__btn--primary"
                                onClick={() => saveEdit(ev)}
                                disabled={isSaving}
                              >
                                {isSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {events.length > 0 && viewMode === "grid" && (
        <div className="edit-events-tab__grid">
          {events.map((ev) => {
            const id = ev.id;
            const thumbSrc = buildThumbImgUrl(ev.imgKey);
            const origSrc = buildOriginalImgUrl(ev.imgKey);
            const fallbackActivity = activityFromSocialEventId(id);
            return (
              <button
                key={id}
                type="button"
                className="edit-events-tab__card"
                onClick={() => startEdit(ev)}
                title={ev.title || "(untitled)"}
              >
                {thumbSrc || origSrc ? (
                  <img
                    className="edit-events-tab__card-thumb"
                    src={thumbSrc || origSrc}
                    onError={
                      thumbSrc
                        ? (e) => handleImgErrorFallback(e, origSrc)
                        : undefined
                    }
                    alt={ev.title || "(untitled)"}
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="edit-events-tab__card-thumb edit-events-tab__card-thumb--placeholder"
                    aria-hidden
                  >
                    {fallbackActivity || "no image"}
                  </div>
                )}
                <div className="edit-events-tab__card-body">
                  <div className="edit-events-tab__card-title">
                    {ev.title || "(untitled)"}
                  </div>
                  <div className="edit-events-tab__card-meta">
                    <span>{ev.date || "—"}</span>
                    {ev.time ? (
                      <span className="edit-events-tab__card-meta-time">
                        {" · "}
                        {ev.time}
                      </span>
                    ) : null}
                  </div>
                  {ev.privateLocation ? (
                    <div
                      className="edit-events-tab__card-loc"
                      title={ev.privateLocation}
                    >
                      {ev.privateLocation}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Grid-view edit modal — same form as the inline-row, just in a modal
          container. We reuse `editingId` + `editDraft`; the only thing the
          two views differ on is where the form is rendered. */}
      {viewMode === "grid" && editingId && editDraft && (() => {
        const ev = (events || []).find((e) => e.id === editingId);
        if (!ev) return null;
        const isSaving = savingId === ev.id;
        return (
          <div
            className="edit-events-tab__modal-backdrop"
            onClick={isSaving ? undefined : cancelEdit}
          >
            <div
              className="edit-events-tab__modal edit-events-tab__modal--wide"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Edit event"
            >
              <button
                type="button"
                className="edit-events-tab__modal-close"
                onClick={cancelEdit}
                disabled={isSaving}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
              <h3 style={{ margin: 0, fontSize: 18 }}>Edit event</h3>

              <div className="edit-events-tab__modal-body">
                <div className="edit-events-tab__modal-imgwrap">
                  {editDraft.imgKey ? (
                    <img
                      src={buildOriginalImgUrl(editDraft.imgKey)}
                      alt={editDraft.title || "(untitled)"}
                      className="edit-events-tab__modal-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="edit-events-tab__modal-img-placeholder">
                      No image selected
                    </div>
                  )}
                </div>

                <div className="edit-events-tab__modal-fields">
                  {renderEditForm(ev)}
                </div>
              </div>

              <div className="edit-events-tab__modal-actions edit-events-tab__modal-actions--split">
                <button
                  type="button"
                  className="edit-events-tab__btn edit-events-tab__btn--danger"
                  onClick={() => requestDelete(ev)}
                  disabled={isSaving || deletingId === ev.id}
                >
                  {deletingId === ev.id ? "Deleting…" : "Delete"}
                </button>
                <div className="edit-events-tab__modal-actions">
                  <button
                    type="button"
                    className="edit-events-tab__btn"
                    onClick={cancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="edit-events-tab__btn edit-events-tab__btn--primary"
                    onClick={() => saveEdit(ev)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {!error && (
        <div className="edit-events-tab__load-older">
          <button
            type="button"
            className="edit-events-tab__btn edit-events-tab__btn--ghost"
            onClick={loadOlder}
            disabled={loading}
          >
            Load older events (+7 days)
          </button>
        </div>
      )}

      {confirmingDeleteId && (() => {
        const ev = (events || []).find((e) => e.id === confirmingDeleteId);
        if (!ev) return null;
        const ready = confirmText.trim().toLowerCase() === "delete";
        const busy = deletingId === confirmingDeleteId;
        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={busy ? undefined : cancelDelete}
          >
            <div
              style={{
                background: "#fff",
                color: "#212529",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "440px",
                width: "92%",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, color: "#dc3545" }}>Delete this event?</h3>
              <p style={{ margin: "8px 0" }}>
                <strong>{ev.title || ev.id}</strong>
              </p>
              <p style={{ margin: "8px 0", fontSize: "13px", color: "#6c757d" }}>
                This permanently removes the row from socialEvents. This cannot be undone.
                Type <strong>delete</strong> to confirm.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  marginTop: "8px",
                  boxSizing: "border-box",
                }}
                autoFocus
              />
              <div style={{ marginTop: "16px", textAlign: "right" }}>
                <button
                  type="button"
                  className="edit-events-tab__btn"
                  onClick={cancelDelete}
                  disabled={busy}
                >
                  Cancel
                </button>{" "}
                <button
                  type="button"
                  className="edit-events-tab__btn edit-events-tab__btn--danger"
                  onClick={() => performDelete(ev)}
                  disabled={!ready || busy}
                >
                  {busy ? "Deleting…" : "Delete event"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default EditEventsTab;
